import { useEffect, useId, useState } from 'react'
import type { ChangeEvent } from 'react'
import { CloseOutlined, FileTextOutlined, UploadOutlined } from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/ui/Modal'
import { UploadTooLargeError, assertWithinMaxBytes, putFileToS3 } from '../../uploads/s3Upload'
import { TECHNICAL_DRAWING_ACCEPT, TECHNICAL_DRAWING_CONTENT_TYPE } from '../attachments/types'
import type { PartAttachment, PartAttachmentFileKind } from '../attachments/types'
import { defaultPartAttachmentsService } from '../attachments/usePartAttachments'
import type { PartAttachmentsService } from '../attachments/usePartAttachments'
import type { PartProcess } from '../edit-part-config-service'
import {
  type CreatePartAuthoredRevisionRequest,
  type PartAuthoredRevision,
  type PartRevisionService,
  defaultPartRevisionService,
} from '../part-revision-service'
import type { PartDetail } from '../types'

export interface CreateRevisionModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly part: PartDetail
  /** Called with the minted `PartAuthoredRevision` once the D2 handler
   * confirms it — the caller owns refreshing its own listing/detail state
   * (same posture as `EditConfigurationModal.onSaved`); this modal never
   * mutates `part` itself. */
  readonly onCreated?: (revision: PartAuthoredRevision) => void
  /** Injectable for tests / future service swap. */
  readonly service?: PartRevisionService
  /** Injectable for tests — the SAME B2/B3 presign→PUT→persist seam
   * `PartAttachmentsSection` uses, reused here for the dual upload tiles. */
  readonly attachmentsService?: PartAttachmentsService
}

/**
 * "Create revision" modal (UID-167/D3, `revModal` — `Fictiv Platform.dc.html`
 * ~L3563-3626): dual upload tiles (3D model + drawing) using the SAME B2/B3
 * presign→PUT→persist flow `PartAttachmentsSection` (B3) already ships, a
 * collapsible "Update requirements (optional)" disclosure reusing the C0/C3
 * requirement-field vocabulary, and a REQUIRED change note — then calls the
 * D2 `POST /parts/{partId}/revisions` handler with the two uploaded
 * attachments' ids (never a server-only object key).
 *
 * CONTRACT GAP (flagged, not guessed around): the D0 contract's `PartDetail`
 * carries no field for "how many authored revisions already exist" and no
 * list endpoint is wired into the generated client, so this modal cannot
 * honestly compute or display "Rev {nextRev}/{curRev}" the way the mock's
 * `revModal.nextRev`/`curRev` do — showing invented numbers would be a false
 * claim (the same posture `EditConfigurationModal`'s DFM-freshness note
 * takes: never assert something the wire data doesn't back). The sub-copy
 * below is deliberately number-free; once created, the ACTUAL
 * `revisionLabel` the server minted is shown in the success banner, because
 * that number is real. See this story's handoff notes for the suggested D
 * follow-up (add a `nextRevisionLabel`-shaped field to `PartDetail`, or a
 * `GET /parts/{partId}/revisions` list operation to the contract).
 */
export function CreateRevisionModal({
  open,
  onClose,
  part,
  onCreated,
  service = defaultPartRevisionService,
  attachmentsService = defaultPartAttachmentsService,
}: CreateRevisionModalProps) {
  const [model, setModel] = useState<TileState>(EMPTY_TILE)
  const [drawing, setDrawing] = useState<TileState>(EMPTY_TILE)
  const [process, setProcess] = useState<PartProcess | ''>('')
  const [quantity, setQuantity] = useState('')
  const [material, setMaterial] = useState('')
  const [finish, setFinish] = useState('')
  const [tolerance, setTolerance] = useState('')
  const [changeNote, setChangeNote] = useState('')
  const [changeNoteError, setChangeNoteError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Readonly<Record<string, string>>>({})
  const [banner, setBanner] = useState<{ kind: 'error'; message: string } | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [created, setCreated] = useState<PartAuthoredRevision | null>(null)

  useEffect(() => {
    if (!open) return
    setModel(EMPTY_TILE)
    setDrawing(EMPTY_TILE)
    setProcess('')
    setQuantity('')
    setMaterial('')
    setFinish('')
    setTolerance('')
    setChangeNote('')
    setChangeNoteError(null)
    setFieldErrors({})
    setBanner(null)
    setSubmitting(false)
    setCreated(null)
  }, [open])

  const modelInputId = useId()
  const modelErrorId = useId()
  const drawingInputId = useId()
  const drawingErrorId = useId()
  const processId = useId()
  const quantityId = useId()
  const materialId = useId()
  const finishId = useId()
  const toleranceId = useId()
  const changeNoteId = useId()
  const changeNoteErrorId = useId()

  async function handleSelectFile(kind: TileKind, file: File) {
    const validationError = validateExtension(kind, file)
    if (validationError) {
      setTile(kind, { ...EMPTY_TILE, status: 'error', filename: file.name, error: validationError })
      return
    }

    setTile(kind, {
      status: 'uploading',
      filename: file.name,
      progress: 0,
      error: null,
      attachment: null,
    })

    try {
      const ticket = await attachmentsService.presignUpload(part.sourceable.id, {
        fileKind: fileKindFor(kind),
        filename: file.name,
        contentType: contentTypeFor(kind, file),
        sizeBytes: file.size,
      })
      assertWithinMaxBytes(file, ticket)
      await putFileToS3(file, ticket, {
        onProgress: (progress) => updateTile(kind, { progress }),
      })
      updateTile(kind, { status: 'persisting', progress: 100 })
      const attachment = await attachmentsService.createAttachment(
        part.sourceable.id,
        ticket.uploadId,
      )
      setTile(kind, { status: 'done', filename: file.name, progress: 100, error: null, attachment })
    } catch (error) {
      const message =
        error instanceof UploadTooLargeError
          ? error.message
          : error instanceof Error
            ? error.message
            : 'Upload failed. Try again.'
      setTile(kind, {
        status: 'error',
        filename: file.name,
        progress: 0,
        error: message,
        attachment: null,
      })
    }
  }

  function setTile(kind: TileKind, next: TileState) {
    if (kind === 'model') setModel(next)
    else setDrawing(next)
  }
  function updateTile(kind: TileKind, patch: Partial<TileState>) {
    if (kind === 'model') setModel((prev) => ({ ...prev, ...patch }))
    else setDrawing((prev) => ({ ...prev, ...patch }))
  }

  function handleFileInputChange(kind: TileKind) {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      event.target.value = ''
      if (!file) return
      void handleSelectFile(kind, file)
    }
  }

  function handleClearTile(kind: TileKind) {
    setTile(kind, EMPTY_TILE)
  }

  async function handleSubmit() {
    setBanner(null)

    const noteError = validateChangeNote(changeNote)
    setChangeNoteError(noteError)

    const quantityError = validateQuantity(quantity)
    setFieldErrors(quantityError ? { quantity: quantityError } : {})

    const modelMissing = model.status !== 'done' || !model.attachment
    const drawingMissing = drawing.status !== 'done' || !drawing.attachment
    if (modelMissing) {
      updateTile('model', {
        error: model.status === 'error' ? model.error : 'Upload a 3D model file to continue.',
      })
    }
    if (drawingMissing) {
      updateTile('drawing', {
        error: drawing.status === 'error' ? drawing.error : 'Upload a drawing file to continue.',
      })
    }
    if (modelMissing || drawingMissing || noteError || quantityError) {
      return
    }

    const request: CreatePartAuthoredRevisionRequest = {
      // biome-ignore lint/style/noNonNullAssertion: guarded by `modelMissing`/`drawingMissing` above.
      modelAttachmentId: model.attachment!.id,
      // biome-ignore lint/style/noNonNullAssertion: guarded by `modelMissing`/`drawingMissing` above.
      drawingAttachmentId: drawing.attachment!.id,
      changeNote,
      ...(process !== '' && { process }),
      ...(quantity.trim() !== '' &&
        Number.isFinite(Number(quantity)) && { quantity: Number(quantity) }),
      ...(material.trim() !== '' && { material: material.trim() }),
      ...(finish.trim() !== '' && { finish: finish.trim() }),
      ...(tolerance.trim() !== '' && { tolerance: tolerance.trim() }),
    }

    setSubmitting(true)
    try {
      const outcome = await service.createRevision(part.sourceable.id, request)
      if (outcome.ok) {
        setCreated(outcome.revision)
        onCreated?.(outcome.revision)
        return
      }
      if (outcome.kind === 'field_errors') {
        const byField: Record<string, string> = {}
        for (const err of outcome.errors) {
          byField[err.field] = err.detail
        }
        setFieldErrors(byField)
        if (byField.changeNote) setChangeNoteError(byField.changeNote)
        setBanner({
          kind: 'error',
          message: 'One or more revision fields are invalid. See the details below.',
        })
        return
      }
      setBanner({ kind: 'error', message: outcome.message })
    } finally {
      setSubmitting(false)
    }
  }

  const disclosureHasContent =
    process !== '' || quantity !== '' || material !== '' || finish !== '' || tolerance !== ''

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create revision"
      maxWidth={560}
      footer={
        created ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={submitting}>
              Create revision
            </Button>
          </>
        )
      }
    >
      <p className="caption-text part-revision-subcopy">
        Upload a new 3D model or drawing to author a new revision of this part. The current revision
        stays in the library and remains purchasable.
      </p>

      {created ? (
        // biome-ignore lint/a11y/useSemanticElements: <output> requires an enclosing <form>, which this modal isn't — role="status" is the correct live-region posture here (same pattern EditConfigurationModal's save banner uses).
        <p role="status" className="part-config-banner part-config-banner-success">
          {created.revisionLabel} created.
        </p>
      ) : (
        <>
          {banner && (
            <p role="alert" className="part-config-banner part-config-banner-error">
              {banner.message}
            </p>
          )}

          <h3 className="part-config-section-heading part-revision-uploads-heading">
            Upload updated files
          </h3>
          <div className="part-revision-tiles">
            <UploadTile
              kind="model"
              label="3D model"
              hint="STEP, STP, IGES, X_T, STL"
              tile={model}
              inputId={modelInputId}
              errorId={modelErrorId}
              onChange={handleFileInputChange('model')}
              onClear={() => handleClearTile('model')}
            />
            <UploadTile
              kind="drawing"
              label="Drawing"
              hint="PDF technical drawing"
              tile={drawing}
              inputId={drawingInputId}
              errorId={drawingErrorId}
              onChange={handleFileInputChange('drawing')}
              onClear={() => handleClearTile('drawing')}
            />
          </div>

          <details className="part-revision-requirements" open={disclosureHasContent}>
            <summary className="part-revision-requirements-summary">
              Update requirements (optional)
            </summary>
            <div className="part-revision-requirements-body">
              <div className="part-config-row">
                <div className="part-config-field">
                  <label htmlFor={processId} className="part-config-field-label">
                    Process
                  </label>
                  <select
                    id={processId}
                    value={process}
                    onChange={(event) => setProcess(event.target.value as PartProcess | '')}
                  >
                    <option value="">No change</option>
                    {PROCESS_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.process && (
                    <FieldError id={`${processId}-error`}>{fieldErrors.process}</FieldError>
                  )}
                </div>
                <div className="part-config-field part-config-field-quantity">
                  <label htmlFor={quantityId} className="part-config-field-label">
                    Default quantity
                  </label>
                  <input
                    id={quantityId}
                    type="number"
                    min={1}
                    value={quantity}
                    onChange={(event) => setQuantity(event.target.value)}
                  />
                  {fieldErrors.quantity && (
                    <FieldError id={`${quantityId}-error`}>{fieldErrors.quantity}</FieldError>
                  )}
                </div>
              </div>
              <div className="part-config-row">
                <div className="part-config-field">
                  <label htmlFor={materialId} className="part-config-field-label">
                    Material
                  </label>
                  <input
                    id={materialId}
                    type="text"
                    value={material}
                    onChange={(event) => setMaterial(event.target.value)}
                  />
                  {fieldErrors.material && (
                    <FieldError id={`${materialId}-error`}>{fieldErrors.material}</FieldError>
                  )}
                </div>
                <div className="part-config-field">
                  <label htmlFor={toleranceId} className="part-config-field-label">
                    Tolerance
                  </label>
                  <input
                    id={toleranceId}
                    type="text"
                    value={tolerance}
                    onChange={(event) => setTolerance(event.target.value)}
                  />
                  {fieldErrors.tolerance && (
                    <FieldError id={`${toleranceId}-error`}>{fieldErrors.tolerance}</FieldError>
                  )}
                </div>
              </div>
              <div className="part-config-field">
                <label htmlFor={finishId} className="part-config-field-label">
                  Finish
                </label>
                <input
                  id={finishId}
                  type="text"
                  value={finish}
                  onChange={(event) => setFinish(event.target.value)}
                />
                {fieldErrors.finish && (
                  <FieldError id={`${finishId}-error`}>{fieldErrors.finish}</FieldError>
                )}
              </div>
            </div>
          </details>

          <div className="part-config-section part-revision-change-note">
            <label htmlFor={changeNoteId} className="part-config-field-label">
              Change note
            </label>
            <textarea
              id={changeNoteId}
              required
              maxLength={CHANGE_NOTE_MAX_LENGTH}
              value={changeNote}
              placeholder="e.g. Updated pocket geometry per DFM feedback"
              aria-describedby={changeNoteError ? changeNoteErrorId : undefined}
              onChange={(event) => {
                setChangeNote(event.target.value)
                if (changeNoteError) setChangeNoteError(null)
              }}
            />
            {changeNoteError && <FieldError id={changeNoteErrorId}>{changeNoteError}</FieldError>}
          </div>
        </>
      )}
    </Modal>
  )
}

function FieldError({ id, children }: { readonly id: string; readonly children: string }) {
  return (
    <p id={id} role="alert" className="part-config-field-error">
      {children}
    </p>
  )
}

type TileKind = 'model' | 'drawing'
type TileStatus = 'empty' | 'uploading' | 'persisting' | 'done' | 'error'

interface TileState {
  readonly status: TileStatus
  readonly filename: string | null
  readonly progress: number
  readonly error: string | null
  readonly attachment: PartAttachment | null
}

const EMPTY_TILE: TileState = {
  status: 'empty',
  filename: null,
  progress: 0,
  error: null,
  attachment: null,
}

const MODEL_EXTENSIONS = ['.step', '.stp', '.iges', '.igs', '.x_t', '.stl']
const CHANGE_NOTE_MAX_LENGTH = 2000

/** `TileKind` is this component's UI-local vocabulary (`'model' | 'drawing'`);
 * the B0/B2 wire contract's `PartAttachmentFileKind` names the drawing kind
 * `technical_drawing` — this maps the former onto the latter at the one call
 * site that crosses the boundary. */
function fileKindFor(kind: TileKind): PartAttachmentFileKind {
  return kind === 'drawing' ? 'technical_drawing' : 'model'
}

function validateExtension(kind: TileKind, file: File): string | null {
  const name = file.name.toLowerCase()
  if (kind === 'drawing') {
    const looksLikePdf =
      file.type === TECHNICAL_DRAWING_CONTENT_TYPE || name.endsWith(TECHNICAL_DRAWING_ACCEPT)
    return looksLikePdf
      ? null
      : `Unsupported file type. Supported file format: ${TECHNICAL_DRAWING_ACCEPT}`
  }
  const looksLikeModel = MODEL_EXTENSIONS.some((ext) => name.endsWith(ext))
  return looksLikeModel
    ? null
    : `Unsupported file type. Supported formats: ${MODEL_EXTENSIONS.map((e) => e.slice(1).toUpperCase()).join(', ')}`
}

/** The server's per-`fileKind` content-type allowlist for `model`
 * (`part-attachment-policy.ts`) does not cover every extension this tile
 * accepts by name (e.g. no dedicated STL/X_T MIME type) — browsers are also
 * unreliable at reporting a `File.type` for CAD formats. `octet-stream` is
 * always allowlisted for `model` (and is the exact fallback the B2 carry-
 * forward mapping itself uses for the same reason, `normalizeCarryForwardAttachment`),
 * so it's the safe default here rather than guessing a MIME type the server
 * might reject with a 415. A drawing always declares the real PDF type. */
function contentTypeFor(kind: TileKind, file: File): string {
  if (kind === 'drawing') return file.type || TECHNICAL_DRAWING_CONTENT_TYPE
  // Model tile ALWAYS declares octet-stream: the server's `model` content-type
  // allowlist (`part-attachment-policy.ts`) does NOT include `application/pdf`, so
  // passing a browser-reported PDF type through here would be rejected with a 415.
  // octet-stream is the one type always allowlisted for `model` (and the exact
  // fallback the B2 carry-forward mapping uses), so it is unconditionally safe.
  return 'application/octet-stream'
}

function validateChangeNote(value: string): string | null {
  if (value.trim().length === 0) return 'A change note is required.'
  if (value.length > CHANGE_NOTE_MAX_LENGTH) {
    return `Change note must be ${CHANGE_NOTE_MAX_LENGTH} characters or fewer.`
  }
  return null
}

// Mirrors the server's bounded-integer quantity contract (`1..1_000_000`). Caught
// client-side so a non-integer (e.g. "2.5") surfaces as a visible field error rather
// than relying on a round-trip 400.
const QUANTITY_MIN = 1
const QUANTITY_MAX = 1_000_000

function validateQuantity(value: string): string | null {
  const trimmed = value.trim()
  if (trimmed === '') return null
  const parsed = Number(trimmed)
  if (!Number.isInteger(parsed) || parsed < QUANTITY_MIN || parsed > QUANTITY_MAX) {
    return `Default quantity must be a whole number between ${QUANTITY_MIN} and ${QUANTITY_MAX}.`
  }
  return null
}

const PROCESS_OPTIONS: ReadonlyArray<{ readonly id: PartProcess; readonly label: string }> = [
  { id: 'cnc_machining', label: 'CNC machining' },
  { id: 'sheet_metal_fabrication', label: 'Sheet metal fabrication' },
  { id: 'injection_molding', label: 'Injection molding' },
  { id: 'urethane_casting', label: 'Urethane casting' },
  { id: '3d_printing', label: '3D printing' },
]

function UploadTile({
  kind,
  label,
  hint,
  tile,
  inputId,
  errorId,
  onChange,
  onClear,
}: {
  readonly kind: TileKind
  readonly label: string
  readonly hint: string
  readonly tile: TileState
  readonly inputId: string
  readonly errorId: string
  readonly onChange: (event: ChangeEvent<HTMLInputElement>) => void
  readonly onClear: () => void
}) {
  const accept = kind === 'drawing' ? TECHNICAL_DRAWING_ACCEPT : MODEL_EXTENSIONS.join(',')
  const hasError = Boolean(tile.error)
  const filled = tile.status === 'done' && tile.attachment
  const busy = tile.status === 'uploading' || tile.status === 'persisting'

  return (
    <div className="part-revision-tile">
      {filled ? (
        <div className="part-attachments-chip part-attachments-chip-filled">
          <span className="part-attachments-chip-icon" aria-hidden="true">
            <FileTextOutlined />
          </span>
          <span className="part-attachments-chip-body">
            <span className="part-attachments-chip-label">{label}</span>
            <span className="part-attachments-chip-filename">{tile.filename}</span>
          </span>
          <button
            type="button"
            className="icon-btn part-attachments-chip-action"
            onClick={onClear}
            aria-label={`Clear ${label.toLowerCase()} selection`}
          >
            <CloseOutlined aria-hidden="true" />
          </button>
        </div>
      ) : busy ? (
        <div className="part-attachments-chip part-attachments-chip-pending">
          <span className="part-attachments-chip-icon" aria-hidden="true">
            <FileTextOutlined />
          </span>
          <span className="part-attachments-chip-body">
            <span className="part-attachments-chip-filename">{tile.filename}</span>
            <span className="caption-text">
              {tile.status === 'uploading' ? `Uploading… ${tile.progress}%` : 'Saving…'}
            </span>
          </span>
        </div>
      ) : (
        <>
          {/* Only rendered while a file selection is actually possible (empty
              or errored) — once `busy`/`filled`, the input is removed from
              the DOM entirely rather than left focusable-but-unlabeled
              (WCAG 3.3.2, "every field has a visible programmatic label";
              the chip above it is decorative status, not a form field). */}
          <label htmlFor={inputId} className="part-revision-upload-tile">
            <UploadOutlined aria-hidden="true" />
            <span className="part-revision-upload-tile-label">Upload {label.toLowerCase()}</span>
            <span className="caption-text">{hint}</span>
          </label>
          <input
            id={inputId}
            type="file"
            accept={accept}
            className="sr-only"
            onChange={onChange}
            aria-describedby={hasError ? errorId : undefined}
          />
        </>
      )}
      {hasError && <FieldError id={errorId}>{tile.error ?? ''}</FieldError>}
    </div>
  )
}
